import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditCard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Card } from "@shared/models/card-stack"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { each, get, partial } from "lodash-es"
import Patch from "@shared/models/patch-updater"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  cardToReplaceId: string
  cardToPaste: Card
  cardToReplace: Card
}

export default function lessonCardPasteAction(payload: Payload): PreparedAction {
  return {
    name: "LESSON_CARD_PASTE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.cardToReplaceId,
  })

  // Note: This action may change the card's type.
  // If so, the card may end up with extra properties for its type, since the
  // old type's properties are not deleted.
  patch.set("embedded", `type`, payload.cardToPaste.type)
  each(payload.cardToPaste.attributes, (val, key) => {
    if (key === "position") return
    if (key === "standards") {
      // Add the standards without overwriting the current ones.
      patch.addToSet("embedded", "attributes.standards", ["$each", val])
      return
    }
    patch.set("embedded", `attributes.${key}`, val)
  })

  return [patch.patch]
}

function undoPatches(payload: Payload) {
  let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.cardToReplaceId,
  }).set("embedded", `type`, payload.cardToReplace.type)

  each(JSON.parse(JSON.stringify(payload.cardToReplace.attributes)), (val, key) => {
    patch.set("embedded", `attributes.${key}`, val)
  })
  return [patch.patch]
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultLessonModels(payload, findFn, userId)
  let cardToPasteTitle = get(payload.cardToPaste, "attributes.title")
  let cardToReplaceTitle = get(payload.cardToReplace, "attributes.title")
  let titleForAnalytics
  let adverbialObjectSpecifier

  if (models.cardStackType === "unit") {
    titleForAnalytics =
      payload.cardToPaste.type === "card-standards"
        ? "Lesson Card - Paste Standards - Unit"
        : "Lesson Card - Paste - Unit"
    adverbialObjectSpecifier = "unit"
  } else {
    titleForAnalytics =
      payload.cardToPaste.type === "card-standards"
        ? "Lesson Card - Paste Standards"
        : "Lesson Card - Paste"
    adverbialObjectSpecifier = "lesson"
  }
  let context = defaultLessonContext(payload, models)
  context.cardToPasteTitle = cardToPasteTitle
  context.cardToPasteId = payload.cardToPaste.id
  context.cardToReplaceTitle = cardToReplaceTitle
  context.cardToReplaceId = payload.cardToReplaceId
  context.editType = "paste"

  let newEvent: LessonEditCard = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "cards",

    adverbial_verb: "pasting",
    adverbial_object_type: payload.cardToPaste.type === "card-standards" ? "standard" : "card",
    adverbial_object_specifier: adverbialObjectSpecifier,
    adverbial_object_id: payload.cardToPaste.id,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
  }

  return {
    title: "Pasted a Lesson Card",
    titleForAnalytics: titleForAnalytics,
    description: `Pasted "${cardToPasteTitle}" on top of "${cardToReplaceTitle}" in your ${context.courseTitle} lesson on ${context.date}`,
    context: context,
    activity: defaultLessonActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
